"use client";

import { ArrowLeftRight, Banknote, Code2, CreditCard } from "lucide-react";
import { PopText, Reveal } from "@/components/motion/reveal";
import { FeatureList } from "@/components/landing/beats/feature-list";
import { DemoTradeWidget } from "@/components/landing/demo-trade-widget";
import { GradientText } from "@/components/ui/gradient-text";
import { Spotlight } from "@/components/ui/spotlight";

const FEATURES = [
  { icon: CreditCard, text: "Fiat → any token, on any chain" },
  { icon: Banknote, text: "Sell anything back to fiat" },
  { icon: ArrowLeftRight, text: "Convert any pair, one widget" },
  { icon: Code2, text: "A single iframe — no SDK" },
];

function CodeBlock() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-card/70 backdrop-blur-xl shadow-[0_8px_40px_-16px_rgba(0,0,0,0.5)]">
      <div className="flex items-center gap-2 border-b border-border/60 px-4 py-2.5">
        <span className="size-3 rounded-full bg-rose-400/70" />
        <span className="size-3 rounded-full bg-amber-400/70" />
        <span className="size-3 rounded-full bg-emerald-400/70" />
        <span className="ml-2 text-xs text-muted-foreground">widget.tsx</span>
      </div>
      <pre className="overflow-x-auto p-5 text-sm leading-relaxed">
        <code className="font-mono">
          <span className="text-primary">const</span>
          <span className="text-foreground"> moonsuiteParams </span>
          <span className="text-foreground/60">= </span>
          <span className="text-primary">new</span>
          <span className="text-sky-400"> URLSearchParams</span>
          <span className="text-foreground/60">({"{"}</span>
          {"\n  "}
          <span className="text-foreground/80">partner</span>
          <span className="text-foreground/60">: </span>
          <span className="text-emerald-400">&apos;TROLL&apos;</span>
          <span className="text-foreground/60">,</span>
          {"\n  "}
          <span className="text-foreground/80">theme</span>
          <span className="text-foreground/60">: </span>
          <span className="text-emerald-400">&apos;light&apos;</span>
          <span className="text-foreground/60">,</span>
          {"\n"}
          <span className="text-foreground/60">{"});"}</span>
          {"\n\n"}
          <span className="text-primary">const</span>
          <span className="text-foreground"> iframeSrc </span>
          <span className="text-foreground/60">=</span>
          {"\n  "}
          <span className="text-emerald-400">
            {"`${MOONSUITE_BASE_URL}/token/${TOKEN_MINT}?${moonsuiteParams.toString()}`"}
          </span>
          <span className="text-foreground/60">;</span>
          {"\n\n"}
          <span className="text-primary">return</span>
          <span className="text-foreground/60"> {"<"}</span>
          <span className="text-sky-400">iframe</span>
          <span className="text-foreground/80"> src</span>
          <span className="text-foreground/60">=</span>
          <span className="text-foreground">{"{iframeSrc}"}</span>
          <span className="text-foreground/60">{" />;"}</span>
        </code>
      </pre>
    </div>
  );
}

function WidgetFrame() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-card/60 shadow-glow backdrop-blur-xl">
      <div className="flex items-center gap-2 border-b border-border/60 px-4 py-2.5">
        <span className="size-3 rounded-full bg-rose-400/70" />
        <span className="size-3 rounded-full bg-amber-400/70" />
        <span className="size-3 rounded-full bg-emerald-400/70" />
        <div className="ml-2 flex-1 truncate rounded-md bg-muted/60 px-3 py-1 text-center text-xs text-muted-foreground">
          your.app/token
        </div>
      </div>
      <div className="relative grid min-h-[30rem] place-items-center p-6">
        <DemoTradeWidget />
      </div>
    </div>
  );
}

export function WidgetEmbed() {
  return (
    <section id="widget" className="relative overflow-hidden px-4 py-16 sm:py-20">
      <Spotlight fill="rgba(168,85,247,0.16)" className="left-1/2 top-0 -translate-x-1/2" />

      <Reveal className="mx-auto mb-12 max-w-2xl text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Trade widget</p>
        <h2 className="mt-3 text-balance text-4xl font-semibold tracking-tight sm:text-5xl">
          <PopText className="block">Fiat to anything.</PopText>
          <PopText className="block" delay={0.18}>
            <GradientText>And back.</GradientText>
          </PopText>
        </h2>
        <p className="mx-auto mt-4 hidden max-w-xl text-lg text-muted-foreground md:block">
          Drop the full trade widget into any page with a single iframe — buy any token with fiat,
          sell back to fiat, or convert any pair. No SDK, no wallet wiring.
        </p>
      </Reveal>

      <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2">
        <Reveal className="min-w-0 space-y-6">
          <CodeBlock />
          <FeatureList features={FEATURES} />
        </Reveal>

        <Reveal delay={0.1} className="min-w-0">
          <WidgetFrame />
        </Reveal>
      </div>
    </section>
  );
}
